import { Box } from '@mui/material';
import { Button, FormErrors, Modal, ModalProps, Select } from 'components/ui';
import { ErrorMessages, updateGraduationYearFormSchema } from 'forms';
import { useAppForm, useProfile } from 'hooks';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useProfileApi } from 'services/api';
import { throttle } from 'lodash';
import { useSnackbar } from 'notistack';
import { z } from 'zod';

export interface UpdateGraduationDateModalProps
  extends Pick<ModalProps, 'open' | 'onClose'> {}

export const UpdateGraduationDateModal = ({
  open,
  onClose,
}: UpdateGraduationDateModalProps): ReactElement => {
  const { profile } = useProfile({ revalidateOnMount: true });

  const year = profile?.graduationYear;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useAppForm(updateGraduationYearFormSchema);

  const [isSubmitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) setSubmitting(false);

    if (!profile) return;

    reset({
      year,
    });
  }, [profile, open]);

  const profileApi = useProfileApi();

  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = async (
    data: z.TypeOf<typeof updateGraduationYearFormSchema>,
  ) => {
    if (!data.year) return;

    setSubmitting(true);
    const result = await profileApi.updateGraduationDate(data.year);

    if (result.success) return onClose?.();

    setSubmitting(false);
    enqueueSnackbar(result.error.message, { variant: 'error' });
  };

  const throttledOnSubmit = throttle(onSubmit, 200, { leading: true });

  const handleFormSubmit = (
    data: z.TypeOf<typeof updateGraduationYearFormSchema>,
  ) => {
    throttledOnSubmit(data);
  };

  const yearOptions = useMemo(() => {
    const yearOptions = [<option key="">--</option>];
    for (let i = 2023; i <= new Date().getUTCFullYear() + 11; i++) {
      yearOptions.push(
        <option key={i} value={i}>
          {i}
        </option>,
      );
    }

    return yearOptions;
  }, []);

  return (
    <Modal title="Update Your Graduation Year" open={open} onClose={onClose}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Box
          style={{ width: 600, maxWidth: '80vw' }}
          display="flex"
          alignItems="flex-end"
        >
          <Box width={'100%'}>
            <Select
              label="Graduation Year"
              placeholder="Select Year"
              hasError={!!errors.year}
              {...register('year', { valueAsNumber: true })}
              sx={{
                '& .input': {
                  '&[value=""]': {
                    color: '#23305F',
                  },
                },
              }}
            >
              {yearOptions}
            </Select>
            {!!errors.year && (
              <FormErrors sx={{ whiteSpace: 'unset', width: '100%' }}>
                {ErrorMessages.GraduationYear.INVALID}
              </FormErrors>
            )}
          </Box>
        </Box>
        <Box mt={30} display="flex">
          <Box mr={30}>
            <Button minWidth={100} inverted onClick={onClose}>
              Cancel
            </Button>
          </Box>
          <Button
            minWidth={100}
            type="submit"
            variant="vibrant"
            disabled={isSubmitting}
          >
            Save
          </Button>
        </Box>
      </form>
    </Modal>
  );
};
